/**
 * The customer's face, wherever the console shows a customer.
 *
 * ## Why this is a component and not an `<img src>`
 *
 * The photo route is **authenticated**. A customer's face is not a public asset like the brand
 * logo or a bank mark, so fetching it costs a bearer token, and a plain `<img>` has no way to
 * send one. The bytes come through `AdminApi.getUserPhoto`, which is the same
 * `transport.sendMedia` machinery a support attachment already uses: raw bytes, never
 * `JSON.parse`d, wrapped in an object URL with a `revoke` the caller owns. There is deliberately
 * not a second mechanism for this — the mobile app's `ProfilePhoto` does the same thing with a
 * data URI because React Native has no `URL.createObjectURL`, and that is the only difference.
 *
 * ## What it does when there is no photo, which is most of the time
 *
 * The customer's initial on a coloured disc. **Never a broken image, never a question mark, and
 * never an empty circle.** Every failure lands there: no avatar on the record, a 404 from the
 * route, a network error, a token that expired mid-fetch. A money screen with a broken image on
 * it looks like a money screen that is broken.
 *
 * The disc colour is derived from the customer id, so the same person is the same colour on the
 * users table, on their detail page and in a support thread. That is worth having: an operator
 * with three tabs open can see at a glance they are looking at one customer, and a colour that
 * repainted on every sort would teach the opposite.
 *
 * ## When it does not fetch at all
 *
 * Only when `avatar` is a real object. `avatar` is absent on an API that has not shipped the
 * field (see `contracts/pending.ts`) and null for a customer who never set a photo, and firing a
 * request per row for either would be twenty-four guaranteed 404s every time the Users table
 * loads. Absent means lettermark, quietly and immediately.
 */
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../auth/AuthContext.js';
import type { MediaObject } from '../api/client.js';
import type { Avatar } from '../contracts/types.js';
import { avatarTone, initialFor } from '../lib/contact.js';

export function AdminAvatar({
  userId,
  name,
  avatar,
  size = 36,
  className,
}: {
  readonly userId: string;
  /** Whatever the console knows the customer by, in the order it would recognise them. */
  readonly name?: string | null;
  readonly username?: string | null;
  readonly avatar?: Avatar | null | undefined;
  readonly size?: number;
  readonly className?: string;
}): JSX.Element {
  const { api } = useAuth();
  const [url, setUrl] = useState<string | null>(null);
  const held = useRef<MediaObject | null>(null);

  // The version stamp, so replacing a photo refetches and nothing else does. Falling back to the
  // path keeps the effect keyed on *something* that changes when the photo does, for a server
  // that sends an avatar with no `updatedAt`.
  const version = avatar?.updatedAt ?? avatar?.path ?? null;

  useEffect(() => {
    // Release the previous photo before anything else, including on the way to showing none.
    held.current?.revoke();
    held.current = null;
    setUrl(null);

    if (avatar === undefined || avatar === null) return;

    let live = true;
    api
      .getUserPhoto(userId, version ?? undefined)
      .then((media) => {
        if (!live) {
          media.revoke();
          return;
        }
        held.current = media;
        setUrl(media.url);
      })
      .catch(() => {
        // A photo that will not load is not worth an error state anywhere in this console.
        // The lettermark stands, and it is the same mark the row would have shown anyway.
      });

    return () => {
      live = false;
    };
    // `avatar` itself is not a dependency: it is a fresh object on every render of a parent that
    // re-fetches, which would restart this effect forever. `version` is the value that actually
    // changes when the photo does.
  }, [api, userId, version, avatar]);

  useEffect(() => {
    return () => {
      held.current?.revoke();
      held.current = null;
    };
  }, []);

  const initial = initialFor(name, userId);
  const tone = avatarTone(userId);
  const label = name === null || name === undefined || name.trim() === ''
    ? 'Customer, no name on file'
    : `${name}, profile photo`;

  return (
    <span
      className={`admin-avatar admin-avatar--t${String(tone)}${className === undefined ? '' : ` ${className}`}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      role="img"
      aria-label={label}
      title={name ?? undefined}
    >
      {url === null ? <span aria-hidden>{initial}</span> : <img src={url} alt="" />}
    </span>
  );
}
